// @flow

import React, { Component } from 'react';
import {
    Alert,
    Badge,
    Button,
    Col,
    FormGroup,
    Input,
    Label,
    Row,
    Spinner,
} from 'reactstrap';

import ContactRequest from '../../entities/models/ContactRequest';
import { DescendingSortOrder } from '../../entities/types';
import { getJsonRequestBody, jsonGetRequest, jsonPatchRequest, jsonPostRequest } from '../../store/requests';
import StyleClasses from '../../styles/StyleClasses';
import {
    buildAssignToCurrentAdminPatch,
    buildGoogleCalendarUrl,
    buildGmailComposeUrl,
    buildInitialDraft,
    buildQuickReplyUpdate,
    formatTimestamp,
    fromDateInputValue,
    getActiveStatuses,
    getRequestPreview,
    getStatusColor,
    getVisibleQuickActions,
    isAssigned,
    isTerminal,
    needsAttention,
    getTerminalStatuses,
} from './contactRequestDashboardUtils';

const filterOptions = {
    attention: 'Needs Attention',
    assigned: 'Assigned',
    active: 'Active',
    closed: 'Closed',
    all: 'All',
};

const activeStatuses = getActiveStatuses();
const terminalStatuses = getTerminalStatuses();

class ContactRequestDashboard extends Component<{}, any> {
    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            error: null,
            requests: [],
            currentAdmin: null,
            selectedFilter: 'attention',
            savingId: null,
            drafts: {},
            expanded: {},
            reminderRunResult: null,
        };
    }

    componentDidMount() {
        this.load();
    }

    getSortParams() {
        return {
            field: ContactRequest.fields.createdAt,
            fieldName: 'createdAt',
            order: DescendingSortOrder,
        };
    }

    load = () => {
        this.setState({ loading: true, error: null });

        Promise.all([
            jsonGetRequest(ContactRequest.endpoints.list(null, this.getSortParams(), { page: 1, perPage: 100 })),
            jsonGetRequest('admins/me'),
        ])
            .then(([records, currentAdmin]) => {
                const requests = records.map((record) => new ContactRequest(record));
                const drafts = {};

                requests.forEach((request) => {
                    drafts[request.id] = buildInitialDraft(request, currentAdmin);
                });

                this.setState({
                    loading: false,
                    requests,
                    currentAdmin,
                    drafts,
                });
            })
            .catch((error) => {
                this.setState({
                    loading: false,
                    error: error.toString(),
                });
            });
    };

    runReminderSweep = () => {
        this.setState({ reminderRunResult: null });

        jsonPostRequest('contact-requests/reminders/run', getJsonRequestBody({}))
            .then((result) => {
                this.setState({ reminderRunResult: result });
            })
            .catch((error) => {
                this.setState({ reminderRunResult: { error: error.toString() } });
            });
    };

    setDraftValue = (requestId: string, fieldName: string, value: any) => {
        this.setState({
            drafts: {
                ...this.state.drafts,
                [requestId]: {
                    ...this.state.drafts[requestId],
                    [fieldName]: value,
                },
            },
        });
    };

    toggleExpanded = (requestId: string) => {
        this.setState({
            expanded: {
                ...this.state.expanded,
                [requestId]: !this.state.expanded[requestId],
            },
        });
    };

    updateRequestState = (updatedRecord: Object, options: { keepExpanded?: boolean } = {}) => {
        const updatedRequest = new ContactRequest(updatedRecord);

        this.setState({
            savingId: null,
            requests: this.state.requests.map((candidate) => (candidate.id === updatedRequest.id ? updatedRequest : candidate)),
            drafts: {
                ...this.state.drafts,
                [updatedRequest.id]: buildInitialDraft(updatedRequest, this.state.currentAdmin),
            },
            expanded: options.keepExpanded ? {
                ...this.state.expanded,
                [updatedRequest.id]: true,
            } : this.state.expanded,
        });
    };

    patchRequest = (request: ContactRequest, body: Object, options: { keepExpanded?: boolean } = {}) => {
        this.setState({ savingId: request.id, error: null });

        return jsonPatchRequest(ContactRequest.endpoints.info(request.id), getJsonRequestBody(body))
            .then((updatedRecord) => {
                this.updateRequestState(updatedRecord, options);
                return updatedRecord;
            })
            .catch((error) => {
                this.setState({
                    savingId: null,
                    error: error.toString(),
                });

                throw error;
            });
    };

    assignToCurrentAdmin = (request: ContactRequest) => {
        const patch = buildAssignToCurrentAdminPatch(this.state.currentAdmin);
        if (!patch) {
            return;
        }

        this.patchRequest(request, patch, { keepExpanded: true });
    };

    openQuickReply = (request: ContactRequest) => {
        const draft = this.state.drafts[request.id] || buildInitialDraft(request, this.state.currentAdmin);
        const url = buildGmailComposeUrl(request, draft.replySubject, draft.replyBody);

        this.patchRequest(request, buildQuickReplyUpdate(this.state.currentAdmin))
            .then(() => {
                window.open(url, '_blank', 'noopener,noreferrer');
            })
            .catch(() => {});
    };

    openScheduleCall = (request: ContactRequest) => {
        const draft = this.state.drafts[request.id] || buildInitialDraft(request, this.state.currentAdmin);
        const url = buildGoogleCalendarUrl(request, this.state.currentAdmin, draft.replyBody);
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    saveRequest = (request: ContactRequest) => {
        const draft = this.state.drafts[request.id];
        const body = {};

        if (draft.status !== request.record.status) {
            body.status = draft.status;
        }

        if (draft.assigneeName !== (request.record.assignee_name || '')) {
            body.assignee_name = draft.assigneeName || null;
        }

        if (draft.assigneeEmail !== (request.record.assignee_email || '')) {
            body.assignee_email = draft.assigneeEmail || null;
        }

        if (draft.notes !== (request.record.notes || '')) {
            body.notes = draft.notes || null;
        }

        const nextFollowUpAt = fromDateInputValue(draft.nextFollowUpAt);
        if (nextFollowUpAt !== (request.record.next_follow_up_at || null)) {
            body.next_follow_up_at = nextFollowUpAt;
        }

        if (Object.keys(body).length === 0) {
            return;
        }

        this.patchRequest(request, body, { keepExpanded: true });
    };

    markAcknowledged = (request: ContactRequest) => {
        this.patchRequest(request, {
            status: 'acknowledged',
        });
    };

    markContactedNow = (request: ContactRequest) => {
        this.patchRequest(request, {
            status: 'contacted',
            last_contacted_at: Math.floor(Date.now() / 1000),
        });
    };

    archiveRequest = (request: ContactRequest) => {
        this.patchRequest(request, {
            status: 'closed',
        });
    };

    getFilteredRequests() {
        switch (this.state.selectedFilter) {
            case 'attention':
                return this.state.requests.filter((request) => needsAttention(request));
            case 'assigned':
                return this.state.requests.filter((request) => isAssigned(request) && !terminalStatuses.includes(request.record.status));
            case 'active':
                return this.state.requests.filter((request) => activeStatuses.includes(request.record.status));
            case 'closed':
                return this.state.requests.filter((request) => terminalStatuses.includes(request.record.status));
            default:
                return this.state.requests;
        }
    }

    renderToolbar() {
        return (
            <div className="contact-request-toolbar">
                <div className="contact-request-filter-row">
                    {Object.entries(filterOptions).map(([value, label]) => (
                        <Button
                            key={value}
                            color={this.state.selectedFilter === value ? 'dark' : 'secondary'}
                            outline={this.state.selectedFilter !== value}
                            onClick={() => this.setState({ selectedFilter: value })}
                            className="contact-request-filter-button"
                        >
                            {label}
                        </Button>
                    ))}
                </div>
                <div className="contact-request-toolbar-actions">
                    <Button color="secondary" outline onClick={this.runReminderSweep}>
                        Run Reminder Sweep
                    </Button>
                    <Button color="primary" onClick={this.load}>
                        Refresh
                    </Button>
                </div>
            </div>
        );
    }

    renderReminderResult() {
        const result = this.state.reminderRunResult;
        if (!result) {
            return null;
        }

        if (result.error) {
            return <Alert color="danger">Reminder sweep failed: {result.error}</Alert>;
        }

        return (
            <Alert color="info">
                Reminder sweep complete. 24h reminders: {result.unacked_reminders_sent}. Weekly assignee reminders: {result.assignee_reminders_sent}. Errors: {result.errors}.
            </Alert>
        );
    }

    renderQuickActionButton = (request: ContactRequest, actionId: string, saving: boolean, expanded: boolean) => {
        switch (actionId) {
            case 'acknowledge':
                return (
                    <Button key={actionId} color="info" outline size="sm" onClick={() => this.markAcknowledged(request)} disabled={saving}>
                        Acknowledge
                    </Button>
                );
            case 'assign-to-me':
                return (
                    <Button key={actionId} color="primary" outline size="sm" onClick={() => this.assignToCurrentAdmin(request)} disabled={saving}>
                        Assign To Me
                    </Button>
                );
            case 'mark-contacted':
                return (
                    <Button key={actionId} color="warning" outline size="sm" onClick={() => this.markContactedNow(request)} disabled={saving}>
                        Mark Contacted
                    </Button>
                );
            case 'reply':
                return (
                    <Button key={actionId} color="success" outline size="sm" onClick={() => this.openQuickReply(request)}>
                        Quick Reply
                    </Button>
                );
            case 'close':
                return (
                    <Button key={actionId} color="secondary" outline size="sm" onClick={() => this.archiveRequest(request)} disabled={saving}>
                        Close
                    </Button>
                );
            case 'schedule-call':
                return (
                    <Button key={actionId} color="dark" outline size="sm" onClick={() => this.openScheduleCall(request)}>
                        Schedule Call
                    </Button>
                );
            case 'details':
                return (
                    <Button key={actionId} color="secondary" outline size="sm" onClick={() => this.toggleExpanded(request.id)}>
                        {expanded ? 'Hide Details' : 'Open'}
                    </Button>
                );
            default:
                return null;
        }
    };

    renderRequestRow = (request: ContactRequest) => {
        const draft = this.state.drafts[request.id] || buildInitialDraft(request, this.state.currentAdmin);
        const expanded = this.state.expanded[request.id];
        const saving = this.state.savingId === request.id;
        const visibleActions = getVisibleQuickActions(request, this.state.currentAdmin);
        const assignee = request.record.assignee_name || request.record.assignee_email || 'Unassigned';
        const preview = getRequestPreview(request.record.message);
        const assignedToCurrentAdmin = Boolean(this.state.currentAdmin && request.record.assignee_email === this.state.currentAdmin.email);

        return (
            <div
                key={request.id}
                className={`contact-request-row${needsAttention(request) ? ' is-attention' : ''}${expanded ? ' is-expanded' : ''}`}
            >
                <div className="contact-request-row-summary">
                    <div className="contact-request-row-main">
                        <div className="contact-request-row-header">
                            <div>
                                <h4 className="contact-request-title">{request.record.name}</h4>
                                <div className="contact-request-subtitle">
                                    <a href={`mailto:${request.record.email}`}>{request.record.email}</a>
                                </div>
                            </div>
                            <div className="contact-request-badges">
                                {assignedToCurrentAdmin ? (
                                    <Badge color="primary" pill>
                                        Assigned to you
                                    </Badge>
                                ) : null}
                                {needsAttention(request) ? (
                                    <Badge color="warning" pill>
                                        Attention
                                    </Badge>
                                ) : null}
                                {isAssigned(request) ? (
                                    <Badge color="light" pill>
                                        {assignee}
                                    </Badge>
                                ) : null}
                                <Badge color={getStatusColor(request.record.status)} pill>
                                    {request.statusLabel}
                                </Badge>
                            </div>
                        </div>

                        <div className="contact-request-row-preview">{preview}</div>

                        <div className="contact-request-row-meta">
                            <span><strong>Created:</strong> {formatTimestamp(request.record.created_at)}</span>
                            <span><strong>Source:</strong> {request.record.source}</span>
                            <span><strong>Last Activity:</strong> {formatTimestamp(request.record.last_activity_at)}</span>
                            <span><strong>Next Follow Up:</strong> {formatTimestamp(request.record.next_follow_up_at)}</span>
                            <span><strong>Last Contacted:</strong> {formatTimestamp(request.record.last_contacted_at)}</span>
                        </div>
                    </div>

                    <div className="contact-request-row-actions">
                        {visibleActions.map((actionId) => this.renderQuickActionButton(request, actionId, saving, expanded))}
                    </div>
                </div>

                {expanded ? (
                    <div className="contact-request-editor">
                        <Row>
                            <Col xs="12" md="6">
                                <FormGroup>
                                    <Label for={`status-${request.id}`}>Status</Label>
                                    <Input
                                        id={`status-${request.id}`}
                                        type="select"
                                        value={draft.status}
                                        onChange={(event) => this.setDraftValue(request.id, 'status', event.target.value)}
                                    >
                                        {Object.entries(ContactRequest.statusOptions).map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col xs="12" md="6">
                                <FormGroup>
                                    <Label for={`followup-${request.id}`}>Next Follow Up</Label>
                                    <Input
                                        id={`followup-${request.id}`}
                                        type="date"
                                        value={draft.nextFollowUpAt}
                                        onChange={(event) => this.setDraftValue(request.id, 'nextFollowUpAt', event.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs="12" md="6">
                                <FormGroup>
                                    <Label for={`assignee-name-${request.id}`}>Assignee Name</Label>
                                    <Input
                                        id={`assignee-name-${request.id}`}
                                        type="text"
                                        value={draft.assigneeName}
                                        onChange={(event) => this.setDraftValue(request.id, 'assigneeName', event.target.value)}
                                        placeholder="Sales owner"
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs="12" md="6">
                                <FormGroup>
                                    <Label for={`assignee-email-${request.id}`}>Assignee Email</Label>
                                    <Input
                                        id={`assignee-email-${request.id}`}
                                        type="email"
                                        value={draft.assigneeEmail}
                                        onChange={(event) => this.setDraftValue(request.id, 'assigneeEmail', event.target.value)}
                                        placeholder="owner@billmaster.ca"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <Label for={`notes-${request.id}`}>Internal Notes</Label>
                            <Input
                                id={`notes-${request.id}`}
                                type="textarea"
                                rows="4"
                                value={draft.notes}
                                onChange={(event) => this.setDraftValue(request.id, 'notes', event.target.value)}
                                placeholder="Capture the latest conversation, next step, and commercial context here. These notes stay in backoffice only."
                            />
                        </FormGroup>

                        {!isTerminal(request) ? (
                            <div className="contact-request-quick-compose">
                                <div className="contact-request-compose-title">Quick Reply</div>
                                <FormGroup>
                                    <Label for={`reply-subject-${request.id}`}>Email Subject</Label>
                                    <Input
                                        id={`reply-subject-${request.id}`}
                                        type="text"
                                        value={draft.replySubject}
                                        onChange={(event) => this.setDraftValue(request.id, 'replySubject', event.target.value)}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label for={`reply-body-${request.id}`}>Email Body</Label>
                                    <Input
                                        id={`reply-body-${request.id}`}
                                        type="textarea"
                                        rows="6"
                                        value={draft.replyBody}
                                        onChange={(event) => this.setDraftValue(request.id, 'replyBody', event.target.value)}
                                    />
                                </FormGroup>
                                <div className="contact-request-compose-actions">
                                    <Button color="success" outline onClick={() => this.openQuickReply(request)}>
                                        Open Gmail Draft
                                    </Button>
                                    <Button color="dark" outline onClick={() => this.openScheduleCall(request)}>
                                        Open Google Calendar
                                    </Button>
                                </div>
                            </div>
                        ) : null}

                        <div className="contact-request-message-block">
                            <div className="contact-request-compose-title">Original Message</div>
                            <div className="contact-request-message">{request.record.message}</div>
                        </div>

                        <div className="contact-request-save-row">
                            <small className="text-muted">Last updated by {request.record.last_activity_by || 'system'}.</small>
                            <Button color="success" onClick={() => this.saveRequest(request)} disabled={saving}>
                                {saving ? <Spinner size="sm" /> : 'Save Lead Update'}
                            </Button>
                        </div>
                    </div>
                ) : null}
            </div>
        );
    };

    renderEmptyState() {
        return (
            <div className="contact-request-empty-state">
                <h4>No contact requests match this view.</h4>
                <p className="text-muted">Try a different filter or refresh to pull the latest leads.</p>
            </div>
        );
    }

    render() {
        if (this.state.loading) {
            return (
                <div className={StyleClasses.spacedElement}>
                    <Spinner />
                </div>
            );
        }

        const filteredRequests = this.getFilteredRequests();

        return (
            <div className="contact-request-dashboard">
                <div className={StyleClasses.spacedElement}>
                    <h2>Contact Requests</h2>
                    <p className="text-muted">
                        Website leads stay isolated behind backoffice APIs only. The public site can create them, but only authenticated admins can read or update them here.
                    </p>
                </div>

                <div className={StyleClasses.spacedElement}>{this.renderToolbar()}</div>
                <div className={StyleClasses.spacedElement}>{this.renderReminderResult()}</div>

                {this.state.error ? (
                    <div className={StyleClasses.spacedElement}>
                        <Alert color="danger">{this.state.error}</Alert>
                    </div>
                ) : null}

                <div className="contact-request-inbox">
                    {filteredRequests.length ? filteredRequests.map(this.renderRequestRow) : this.renderEmptyState()}
                </div>
            </div>
        );
    }
}

export default ContactRequestDashboard;